import { MouseEvent, useMemo } from 'react';
import { Box, Button, Modal, useMediaQuery } from '@mui/material';
import { faPlay } from '@fortawesome/pro-solid-svg-icons';
import {
  faArrowLeft,
  faArrowRight,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import ImageGallery, { ReactImageGalleryItem } from 'react-image-gallery';
import 'react-image-gallery/styles/css/image-gallery.css';

export type MediaGalleryItem = {
  type: 'image' | 'video';
  url: string;
  thumbnailUrl: string;
};

type Props = {
  open: boolean;
  items: MediaGalleryItem[];
  startIndex: number;
  onClose: () => void;
};

const renderLeftNav = (
  onClick: React.MouseEventHandler<HTMLElement>,
  disabled: boolean,
) => (
  <Button
    variant="text"
    disabled={disabled}
    onClick={(ev) => {
      ev.stopPropagation();
      onClick(ev);
    }}
    className="w-[50px]"
    aria-label="Previous media"
  >
    <FontAwesomeIcon
      icon={faArrowLeft}
      className={classNames({ 'text-white': !disabled, 'text-gray': disabled })}
    />
  </Button>
);

const renderRightNav = (
  onClick: React.MouseEventHandler<HTMLElement>,
  disabled: boolean,
) => (
  <Button
    variant="text"
    disabled={disabled}
    onClick={(ev) => {
      ev.stopPropagation();
      onClick(ev);
    }}
    className="w-[50px] order-3"
    aria-label="Next media"
  >
    <FontAwesomeIcon
      icon={faArrowRight}
      className={classNames({ 'text-white': !disabled, 'text-gray': disabled })}
    />
  </Button>
);

const toGalleryItem = (item: MediaGalleryItem): ReactImageGalleryItem => ({
  original: item.url,
  thumbnail: item.thumbnailUrl,
  renderItem: () => (
    <div className="flex justify-center items-center md:h-[500px] md:w-[670px]">
      {item.type === 'image' ? (
        <img
          src={item.url}
          className="rounded-[10px] margin-auto object-contain md:max-h-[500px]"
          loading="lazy"
        />
      ) : (
        <video
          src={item.url}
          className="rounded-[10px] max-h-[500px] max-w-[670px]"
          controls={true}
        />
      )}
    </div>
  ),
  renderThumbInner: () => (
    <div className="relative">
      {item.type === 'video' && (
        <div className="absolute bottom-0 left-0 right-0 top-0 grid place-items-center">
          <FontAwesomeIcon icon={faPlay} className="text-white" />
        </div>
      )}
      <img
        src={item.thumbnailUrl}
        className="rounded-[10px] h-[90px] object-cover m-auto"
        loading="lazy"
      />
    </div>
  ),
});

export const MediaGallery = ({ open, items, startIndex, onClose }: Props) => {
  const isMobileView = useMediaQuery('(max-width:600px)');

  const galleryItems = useMemo(() => items.map(toGalleryItem), [items]);

  const onWrapperClick = (ev: MouseEvent<HTMLDivElement>) => {
    ev.stopPropagation();
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      slotProps={{
        backdrop: {
          sx: { backgroundColor: '#151E34', opacity: '0.9 !important' },
        },
      }}
    >
      <Box
        className="flex m-auto h-[100%] w-[100%] md:w-[842px]"
        onClick={onWrapperClick}
      >
        <Box className="flex flex-col m-auto h-[636px] items-center w-[90%] md:w-[100%]">
          <Box className="flex justify-end w-[100%]">
            <Button variant="text" onClick={onClose} aria-label="Close gallery">
              <FontAwesomeIcon icon={faXmark} className="text-white" />
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              margin: 'auto',
              height: 636,
              alignItems: 'center',
              '& .image-gallery > .image-gallery-content > .image-gallery-slide-wrapper':
                { display: 'flex' },
            }}
          >
            <ImageGallery
              items={galleryItems}
              infinite={false}
              showIndex={false}
              showPlayButton={false}
              startIndex={startIndex}
              showFullscreenButton={false}
              showThumbnails={!isMobileView}
              showNav={!isMobileView}
              renderLeftNav={renderLeftNav}
              renderRightNav={renderRightNav}
            />
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};
